import { fetchSpliceSampleFile } from "@/client/utils/createSpliceSampleInputFile";
import { decodeAudioFile } from "@/client/utils/decodeAudio";
import { getChannelData } from "@/client/utils/getChannelData";
import { createScalableWaveform, type ScalableWaveform } from "@/client/utils/waveform";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

/**
 * A catalog preview once it has been fetched and decoded: the file (what a
 * drop delivers and what plays), its waveform (what the row draws) and the
 * duration the audio actually has.
 */
export interface CachedSamplePreview {
    readonly file: File;
    readonly waveform: ScalableWaveform;
    readonly duration_s: number;
}

export interface SamplePreviewCache {
    /** What is already in hand, without starting a fetch. */
    readonly peek: (uuid: string) => CachedSamplePreview | undefined;
    /**
     * Fetches and decodes the preview, once. Concurrent callers for the same
     * sample share one fetch. Rows scrolling into view all call this, so it
     * runs a handful at a time rather than opening a connection per row.
     */
    readonly ensure: (result: SpliceSearchResult) => Promise<CachedSamplePreview | Error>;
}

export interface SamplePreviewCacheOptions {
    /** Entries kept before the least recently used are dropped. */
    readonly maxEntries: number;
    /** Fetch-and-decode jobs allowed in flight at once. */
    readonly maxConcurrent: number;
    /** Widest the waveform will be drawn; the peaks are computed for it. */
    readonly waveformMaxWidth_px: number;
}

const kDefaultOptions: SamplePreviewCacheOptions = {
    maxEntries: 200,
    maxConcurrent: 4,
    waveformMaxWidth_px: 480,
};

const toError = (error: unknown): Error =>
    error instanceof Error ? error : new Error(String(error));

export const createSamplePreviewCache = (
    userOptions: Partial<SamplePreviewCacheOptions> = {},
    loadFile: (result: SpliceSearchResult) => Promise<File> = fetchSpliceSampleFile,
): SamplePreviewCache => {
    const options = { ...kDefaultOptions, ...userOptions };
    // Map iteration order is insertion order, which makes it an LRU once an
    // entry is deleted and re-set on every hit.
    const entries = new Map<string, CachedSamplePreview>();
    const inFlight = new Map<string, Promise<CachedSamplePreview | Error>>();
    const queue: (() => void)[] = [];
    let active = 0;

    const touch = (uuid: string, entry: CachedSamplePreview): void => {
        entries.delete(uuid);
        entries.set(uuid, entry);
        while (entries.size > options.maxEntries) {
            const oldest = entries.keys().next().value;
            if (oldest === undefined) {
                break;
            }
            entries.delete(oldest);
        }
    };

    const peek: SamplePreviewCache["peek"] = (uuid) => {
        const entry = entries.get(uuid);
        if (entry !== undefined) {
            touch(uuid, entry);
        }
        return entry;
    };

    const acquire = (): Promise<void> =>
        new Promise((resolve) => {
            if (active < options.maxConcurrent) {
                active += 1;
                resolve();
                return;
            }
            queue.push(() => {
                active += 1;
                resolve();
            });
        });

    const release = (): void => {
        active -= 1;
        const next = queue.shift();
        next?.();
    };

    const load = async (result: SpliceSearchResult): Promise<CachedSamplePreview | Error> => {
        await acquire();
        try {
            const file = await loadFile(result);
            const audioBuffer = await decodeAudioFile(file);
            const entry: CachedSamplePreview = {
                file,
                waveform: createScalableWaveform(
                    getChannelData(audioBuffer),
                    options.waveformMaxWidth_px,
                ),
                duration_s: audioBuffer.duration,
            };
            touch(result.metadata.uuid, entry);
            return entry;
        } catch (error: unknown) {
            return toError(error);
        } finally {
            release();
        }
    };

    const ensure: SamplePreviewCache["ensure"] = (result) => {
        const { uuid } = result.metadata;
        const cached = peek(uuid);
        if (cached !== undefined) {
            return Promise.resolve(cached);
        }
        const pending = inFlight.get(uuid);
        if (pending !== undefined) {
            return pending;
        }
        const job = load(result).finally(() => {
            inFlight.delete(uuid);
        });
        inFlight.set(uuid, job);
        return job;
    };

    return { peek, ensure };
};
